import React, { useState, useEffect, useRef, useCallback } from 'react';
import { testimonials } from '../data';
import { hapticTap, hapticLight } from '../utils/haptics';

const CFG = {
  '-2': { x: -370, ry:  68, scale: 0.64, opacity: 0.38 },
  '-1': { x: -210, ry:  58, scale: 0.82, opacity: 0.70 },
   '0': { x:    0, ry:   0, scale: 1.00, opacity: 1.00 },
   '1': { x:  210, ry: -58, scale: 0.82, opacity: 0.70 },
   '2': { x:  370, ry: -68, scale: 0.64, opacity: 0.38 },
};

const circOff = (i, active, total) => {
  let d = i - active;
  if (d >  total / 2) d -= total;
  if (d < -total / 2) d += total;
  return d;
};

const Cover = ({ item, offset, onCoverClick }) => {
  const c = CFG[String(offset)];
  return (
    <div
      className={`cf-item${offset === 0 ? ' cf-item--center' : ''}`}
      style={{
        transform: `translateX(${c.x}px) rotateY(${c.ry}deg) scale(${c.scale})`,
        opacity:   c.opacity,
        zIndex:    10 - Math.abs(offset),
        cursor:    'pointer',
      }}
      onClick={() => onCoverClick(offset, item.linkInsert)}
    >
      {/* 3-D book structure */}
      <div className="book-3d">
        {/* Front face — the cover image */}
        <div className="book-front">
          <img
            src={item.authorImg}
            alt={item.authorText}
            className="cf-img"
            draggable={false}
            loading="lazy"
            decoding="async"
          />
        </div>
        {/* Left face — spine / binding */}
        <div className="book-spine" />
        {/* Right face — page edges */}
        <div className="book-pages" />
      </div>

      {/* Reflection below — stays in the flat 2-D layer */}
      <div className="cf-reflection" aria-hidden="true">
        <img
          src={item.authorImg}
          alt=""
          className="cf-refl-img"
          draggable={false}
          loading="lazy"
          decoding="async"
        />
      </div>
    </div>
  );
};

const SliderMain = () => {
  const [active,   setActive]   = useState(0);
  const [paused,   setPaused]   = useState(false);
  const [dragging, setDragging] = useState(false);
  const [inView,   setInView]   = useState(false);
  const total    = testimonials.length;
  const wrapRef  = useRef(null);
  const dragX    = useRef(null);
  const dragDist = useRef(0);

  const go = useCallback((dir, shouldHaptic = true) => {
    if (shouldHaptic) hapticTap();
    setActive(a => (a + dir + total) % total);
  }, [total]);

  useEffect(() => {
    if (!inView || paused || dragging) return;
    const id = setInterval(() => go(1, false), 3600);
    return () => clearInterval(id);
  }, [inView, paused, dragging, go]);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => setInView(entry.isIntersecting),
      { threshold: 0.2, rootMargin: '80px 0px' }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const startDrag = (x) => { dragX.current = x; dragDist.current = 0; setDragging(true); };
  const moveDrag  = (x) => { if (dragX.current === null) return; dragDist.current = x - dragX.current; };
  const endDrag   = () => {
    if (dragX.current === null) return;
    if (Math.abs(dragDist.current) > 44) go(dragDist.current > 0 ? -1 : 1);
    dragX.current = null; dragDist.current = 0;
    setTimeout(() => setDragging(false), 0);
  };

  const onMouseDown  = (e) => { e.preventDefault(); startDrag(e.clientX); };
  const onMouseMove  = (e) => moveDrag(e.clientX);
  const onMouseUp    = ()  => endDrag();
  const onMouseLeave = ()  => { if (dragX.current !== null) endDrag(); };
  const onTouchStart = (e) => startDrag(e.touches[0].clientX);
  const onTouchMove  = (e) => moveDrag(e.touches[0].clientX);
  const onTouchEnd   = ()  => endDrag();

  const onCoverClick = useCallback((offset, link) => {
    if (Math.abs(dragDist.current) > 8) return;
    if (offset === 0) window.open(link, '_blank', 'noopener noreferrer');
    else setActive(a => (a + offset + total) % total);
  }, [total]);

  const book = testimonials[active];

  return (
    <div
      ref={wrapRef}
      className="cf-wrap"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => { setPaused(false); onMouseLeave(); }}
    >
      <div className="cf-clip">
        <div
          className="cf-stage"
          style={{ cursor: dragging ? 'grabbing' : 'grab' }}
          onMouseDown={onMouseDown}
          onMouseMove={onMouseMove}
          onMouseUp={onMouseUp}
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
        >
          <div className="cf-glow" />
          {testimonials.map((item, i) => {
            const off = circOff(i, active, total);
            if (Math.abs(off) > 2) return null;
            return <Cover key={i} item={item} offset={off} onCoverClick={onCoverClick} />;
          })}
        </div>
      </div>

      <div className="cf-meta">
        <button className="cf-arrow" onClick={() => go(-1)} aria-label="Previous">‹</button>
        <div className="cf-info">
          <p className="cf-title">{book.authorText}</p>
          <a href={book.linkInsert} target="_blank" rel="noopener noreferrer" className="cf-link">
            View on Goodreads →
          </a>
        </div>
        <button className="cf-arrow" onClick={() => go(1)} aria-label="Next">›</button>
      </div>

      <div className="cf-dots" role="tablist">
        {testimonials.map((_, i) => (
          <button
            key={i} role="tab"
            aria-selected={i === active}
            aria-label={testimonials[i].authorText}
            className={`cf-dot${i === active ? ' cf-dot--active' : ''}`}
            onClick={() => { hapticLight(); setActive(i); }}
          />
        ))}
      </div>
    </div>
  );
};

export default SliderMain;
